import BaseStore from 'customer-flows/stores/base-store'
import { tracked } from '@glimmer/tracking'
import { getOwner } from '@ember/owner'
import { context } from 'customer-flows/utils/core'
import { DateTime } from 'luxon'
import { createAvailabilityEngine, type EngineArgs } from 'customer-flows/availability/engine'
import type { ResourceOptionQuantity } from 'customer-flows/availability/types'
import { isTodayOrLater, monthRangeFor } from 'customer-flows/availability/ranges'
import { getPricesForResourceOptionFromDates } from 'customer-flows/availability/prices'
import { isAvailableStatus, isBookableStatus } from 'customer-flows/availability/status'
import { groupTimesByLabel } from 'customer-flows/availability/times'

// Off-instance so the engine never lands in the store's own-property iteration (toJSON) and
// closed-over consumers keep the same engine across reads. One engine per store instance.
const engines = new WeakMap<AvailabilityStoreClass, AvailabilityEngine>()

export default class AvailabilityStoreClass extends BaseStore {
  // The calendar's two centers. `settledCenter` is where navigation has come to rest and is the
  // ONLY thing the fetch scope follows; `pendingCenter` is the month a month-scrub is currently
  // over, which the display follows and the fetch scope deliberately does not. Every navigation hop
  // writes `pendingCenter` unconditionally; the debounced `settleFetchScope` promotes it once the
  // burst ends. Non-scrub writers (mount, date-select) go through the `setCenter` command, which
  // moves `settledCenter` and clears any pending hop in one step — so the two can never drift.
  @tracked settledCenter?: Date
  @tracked pendingCenter?: Date
  // Quantities the fetch scope follows; empty until settled, so a +/- streak never re-keys it.
  @tracked settledQuantities: ResourceOptionQuantity[] = []
  @tracked selectedDate?: string // ISO date string (ie. '2020-01-01')
  // Raw user/hydration write target. Read `selectedAvailabilityTime` instead — it falls back to
  // the auto-selected default when this is unset. Writers (component actions, hydrate/time,
  // clearTime) must write this field directly, never the derived getter.
  @tracked selectedTimeOverride?: string | 'Anytime'
  @tracked selectedAvailabilityDuration?: string // The name of the duration (ie. '1 Hour')
  @tracked calendarStartDate?: string
  @tracked numDaysToShow?: number
  @tracked dateHydratedFromParam: boolean = false
  @tracked timeHydratedFromParam: boolean = false

  @context('activity') isRental: ActivityStore['isRental']
  @context('activity:id') activityId: ActivityStore['id']
  @context('program:id') programId: ProgramStore['id']
  @context('tickets') selectedTickets: TicketsStore['selectedTickets']
  @context('tickets') resourceOptionQuantities: TicketsStore['resourceOptionQuantities']
  @context('availability/rental:*') rentalDomain: RentalAvailabilityStore
  @context('availability/activity:*') activityDomain: ActivityAvailabilityStore
  @context('program/booking-flow:timeSelectionMode') timeSelectionMode: BookingFlowStore['timeSelectionMode']
  @context('program/booking-flow:calendarStartDate') programCalendarStartDate: BookingFlowStore['calendarStartDate']

  private get domain(): RentalAvailabilityStore | ActivityAvailabilityStore {
    return this.isRental ? this.rentalDomain : this.activityDomain
  }

  /**
   * The time to display/use: the user's (or hydration's) explicit choice, or — when the time
   * picker is hidden from the user — the auto-selected default. A pure derivation, not a written
   * effect: writers must target `selectedTimeOverride` directly.
   */
  get selectedAvailabilityTime(): string | 'Anytime' | undefined {
    return this.selectedTimeOverride ?? this.autoDefaultTime
  }

  /**
   * First-available-time label, surfaced only once a date is selected and the flow's
   * timeSelectionMode is HIDDEN (no time picker shown to the user) — gating on `selectedDate`
   * avoids resolving a default before there is any availability to derive it from. Prefers
   * 'Anytime' over a specific time, mirroring the time-picker's own display grouping.
   */
  private get autoDefaultTime(): string | 'Anytime' | undefined {
    if (this.timeSelectionMode !== 'HIDDEN' || !this.selectedDate) return undefined

    const filtered = this.getFilteredAvailability({ hideBookedOut: true })
    if (!filtered.length) return undefined

    const grouped = groupTimesByLabel(filtered)
    const labels = Object.keys(grouped)
    if (!labels.length) return undefined

    return grouped['Anytime'] ? 'Anytime' : labels[0]
  }

  // The availability-dates query store. Lazily built so the store's owner is set before the
  // engine (a destroyable child of this store) is created. It owns all of its own writes — reading
  // one of its projections is what schedules a fetch; nothing pushes data into it.
  get availabilityEngine(): AvailabilityEngine {
    let engine = engines.get(this)
    if (!engine) {
      engine = createAvailabilityEngine(this, () => this.engineArgs)
      engines.set(this, engine)
    }
    return engine
  }

  // The fetch scope: follows `settledCenter` and nothing else, so a scrub's in-flight
  // `pendingCenter` hops never reach it. Outside a scrub the two centers are the same month, so
  // this is string-equal to `selectedDateRange` (both resolve through the same `monthRangeFor` with
  // the same overflow/maxDate) — which is what makes "is a scrub in progress?" a derivation rather
  // than a flag. `new Date()` matches `center`'s `DateTime.now()` fallback for the pre-init instant.
  private get anchoredDateRange(): string {
    const anchorPoint = this.settledCenter ?? new Date()
    return monthRangeFor(DateTime.fromJSDate(anchorPoint), {
      overflowDays: this.overflowDaysCount,
      maxDate: this.maxDate
    })
  }

  // Reactive input to the engine: reads tracked store state so the derived dates recompute when
  // the activity, selected range, ticket quantities, program, or channel change.
  private get engineArgs(): EngineArgs {
    return {
      activityId: this.activityId,
      dateRange: this.selectedDateRange,
      anchoredDateRange: this.anchoredDateRange,
      quantities: this.resourceOptionQuantities,
      anchoredQuantities: this.settledQuantities,
      programId: this.programId,
      channelId: this.channelId,
      selectedDate: this.selectedDate,
      minDate: this.minDate?.toISODate() ?? undefined
    }
  }

  // The flow-router service isn't present in unit tests; a missing channel just widens the query.
  private get channelId(): string | undefined {
    const flowRouter = getOwner(this)?.lookup('service:flow-router') as FlowRouter | undefined
    return flowRouter?.queryParams?.channelId
  }

  // Awaitable-shaped view forwarded from the engine, so `.value` / `.loading` / `.error` /
  // `.promise` consumers read the query store's derived projection.
  get availabilityDates() {
    return this.availabilityEngine.availabilityDates
  }

  /** Whether neither family has anything to render yet. */
  get availabilityLoading() {
    return this.availabilityDates.is('unknown')
  }

  /** Whether the calendar has nothing to render yet. */
  get datesLoading() {
    return this.availabilityDates.is('unknown', 'dates')
  }

  /** Whether the selected date has no slots to render yet. */
  get timesLoading() {
    return this.availabilityDates.is('unknown', 'times')
  }

  /**
   * Whether the calendar is showing seed data, which may overstate availability.
   *
   * Surfaces that would otherwise conclude "sold out" gate on this.
   */
  get datesVerifying() {
    return this.availabilityDates.is('approximate', 'dates')
  }

  /** Whether the selected date's slots come from the covering week rather than its own fetch. */
  get timesVerifying() {
    return this.availabilityDates.is('approximate', 'times')
  }

  /** Whether an availability request is in flight. */
  get availabilityFetching() {
    return this.availabilityDates.is('syncing')
  }

  /** The week ranges whose calendar tiles should render a skeleton. */
  get loadingWeekRanges() {
    return this.availabilityDates.unknownWeekRanges
  }

  // Fallback list prices for a ticket, derived from the engine's CURRENT-RANGE live entries — so a
  // hard invalidation (e.g. after a failed order) or an eviction stops condemned/cold prices from
  // being served, which an append-only accumulator could never do. `rangeCapacityDates()` folds the
  // current range's quantity-scoped entries together WITH their all-type seed counterparts (rather
  // than the calendar's exclusive per-week pick), so a kayak-scoped response doesn't drop
  // paddleboard's prices. For use when no live availability times are on the store for the ticket
  // in question.
  getPricesForTicket(resourceOptionId: string): Money[] {
    return getPricesForResourceOptionFromDates(this.availabilityEngine.rangeCapacityDates(), resourceOptionId)
  }

  // First currency seen across the current range's live availability dates' priceRange — a pure
  // derivation, no side effect. Best-effort: reads undefined until at least one live date in range
  // carries a priceRange, which is an acceptable gap for its one consumer (a `?? null` fallback in
  // mixpanel-tracker.ts).
  get currency(): string | undefined {
    for (const date of this.availabilityEngine.rangeCapacityDates()) {
      const currency = date.priceRange?.[0]?.currency
      if (currency) return currency
    }
    return undefined
  }

  /**
   * Returns the availability date object for the selected date, if any.
   */
  get selectedAvailabilityDate(): AvailabilityDate | undefined {
    if (!this.selectedDate) return undefined
    return this.availabilityDates.value?.find((d) => d.date === this.selectedDate)
  }

  /**
   * the absolute upper bound for availability queries when numDaysToShow is set
   * fixed to calendarStartDate if provided, otherwise today.
   */
  get maxDate(): DateTime | undefined {
    if (!this.numDaysToShow) return undefined

    const target = this.calendarStartDate
      ? DateTime.fromISO(this.calendarStartDate)
      : DateTime.now()

    return target.plus({ days: this.numDaysToShow })
  }

  get minDate(): DateTime | undefined {
    if (!this.programCalendarStartDate || !isTodayOrLater(this.programCalendarStartDate)) return undefined

    return DateTime.fromISO(this.programCalendarStartDate).startOf('day')
  }

  // The calendar grid's center month — the DISPLAY center, so it follows an in-flight scrub hop
  // ahead of the settled month. Every grid/range derivation reads this one getter rather than
  // re-inlining the fallback chain; `DateTime.now()` is the pre-init instant, before the
  // date-picker has set a center.
  get center(): DateTime {
    const point = this.pendingCenter ?? this.settledCenter
    return point ? DateTime.fromJSDate(point) : DateTime.now()
  }

  // Whether the date-picker has established a center yet, as opposed to `center` falling back to
  // now. Lets pre-init seeding (a flow's `calendarStartDate`) avoid clobbering a real center.
  get hasCenter(): boolean {
    return !!(this.pendingCenter ?? this.settledCenter)
  }

  // useful to know if we're on the current month so we can decide to show the overflow days or not
  get isViewingCurrentMonth(): boolean {
    const today = DateTime.now()
    return this.center.month === today.month && this.center.year === today.year
  }

  // decides whether to display or not next month's dates on the current month view
  get shouldShowOverflow(): boolean {
    if (!this.isViewingCurrentMonth) return false

    const center = this.center
    const today = DateTime.now()
    const firstDay = center.startOf('month').weekday % 7
    const usedCells = firstDay + (center.daysInMonth ?? 0)
    const totalLines = Math.ceil(usedCells / 7)
    const todayLine = Math.ceil((firstDay + today.day) / 7)

    // 5 line date-picker: trigger on line 5 (last)
    // 6 line date-picker: trigger on line 5 (2nd last)
    return todayLine >= 5 && totalLines >= 5
  }

  // how many next-month days to show in the current month's date-picker component
  get overflowDaysCount(): number {
    if (!this.shouldShowOverflow) return 0

    const center = this.center
    const firstDay = center.startOf('month').weekday % 7
    const rawCount = Math.max(0, 42 - (firstDay + (center.daysInMonth ?? 0)))

    if (rawCount === 0) return 0

    // cap overflow days to not exceed maxDate — tiles beyond maxDate would always be unavailable
    if (this.maxDate) {
      const nextMonthStart = center.plus({ months: 1 }).startOf('month')

      if (this.maxDate < nextMonthStart) return 0

      const maxDay = this.maxDate.diff(nextMonthStart, 'days').days
      return Math.min(rawCount, Math.floor(maxDay) + 1)
    }

    return rawCount
  }

  get selectedDateRange() {
    return monthRangeFor(this.center, { overflowDays: this.overflowDaysCount, maxDate: this.maxDate })
  }

  /**
   * Returns the availability times for the selected date
   * If no date is selected, returns all availability times
   */
  get availabilityForSelectedDate(): AvailabilityTime[] {
    if (!this.selectedDate) {
      return this.availabilityDates.value?.flatMap(({ availabilityTimes }) => availabilityTimes) ?? []
    }

    const availableDates = this.availabilityDates.value?.filter(({ date }) => date === this.selectedDate) ?? []
    return availableDates.flatMap(({ availabilityTimes }) => availabilityTimes ?? []).filter(Boolean)
  }

  /**
   * Returns timeslots for the selected date that match the current time/duration selection,
   * WITHOUT filtering by ticket quantity availability.
   *
   * Used for whitelist computation so that switching to a time where a previously-selected
   * ticket is unavailable still correctly identifies it as non-whitelisted — even though
   * validTimeslots would be empty (because the ticket qty check fails).
   */
  get timeslotsForSelectedTime(): AvailabilityTime[] {
    return this.availabilityForSelectedDate.filter((slot) => {
      const matchesDuration = this.matchesSelectedDuration(slot?.duration)
      if (!matchesDuration) return false

      const matchesTime = this.matchesSelectedTime(slot.time)
      if (!matchesTime) return false

      const isFlexible = slot.duration?.length?.unit === 'FLEXIBLE'

      if (isFlexible && this.selectedAvailabilityTime && this.selectedAvailabilityTime !== 'Anytime') {
        return false
      }

      if (!isFlexible && this.selectedAvailabilityTime === 'Anytime') {
        return false
      }

      return true
    })
  }

  /**
   * Computes the list of bookable timeslots for the selected date (or all dates if no selected date).
   *
   * A timeslot is considered valid if:
   * - it matches the currently selected duration (if any)
   * - it matches the currently selected time (if any, or "Anytime")
   * - it can fulfill the quantity requirements of all confirmed tickets
   */
  get validTimeslots(): AvailabilityTime[] {
    const requiredQtyByTicketId = this.getConfirmedTicketCounts()

    return this.timeslotsForSelectedTime.filter((slot) => {
      const hasAvailability = this.hasAvailabilityForSelectedTickets(slot, requiredQtyByTicketId)
      if (!hasAvailability) return false

      return true
    })
  }

  get hasValidAvailability() {
    return this.availabilityLoading || this.validTimeslots.length > 0
  }

  // returns the number of days in advance of the selected date
  get daysInAdvance(): number {
    if (!this.selectedDate) return 0
    const today = DateTime.now().startOf('day')
    const selected = DateTime.fromISO(this.selectedDate).startOf('day')
    return Math.abs(selected.diff(today, 'days').days)
  }

  /**
   * Returns the availability times for the selected date, filtered by the given criteria.
   *
   * @param filterBy - Optional filter criteria
   * @param filterBy.hideBookedOut - Whether to hide timeslots that are booked out
   */
  getFilteredAvailability(filterBy: { hideBookedOut?: boolean } = {}) {
    return this.availabilityForSelectedDate.filter((slot) => {
      if (
        filterBy.hideBookedOut &&
        (
          !this.isBookableStatus(slot.status) ||
          !this.isAvailableStatus(slot.status)
        )
      )
        return false

      return true
    })
  }

  /**
   * returns the maximum number of spots left for the given time string
   * @param time
   */
  getMaxSpotsLeftForTime(time: string): number {
    const slots = time === 'Anytime'
      ? this.availabilityForSelectedDate.filter((slot) => slot.duration?.length?.unit === 'FLEXIBLE')
      : this.availabilityForSelectedDate.filter((slot) => slot.time === time && slot.duration?.length?.unit !== 'FLEXIBLE')

    return slots
      .reduce((max, slot) => {
        if (!this.isAvailableStatus(slot.status)) return max
        return Math.max(max, this.domain.getMaxSpotsLeftForSlot(slot))
      }, 0)
  }

  /**
   * There will be multiple availability entries for a given time string.
   * This method returns a list of unique statuses found across all availability
   * entries for a given time string.
   */
  getStatusesForTime(time: string): AvailabilityMode[] {
    const uniqueStatuses = new Set<AvailabilityMode>()

    // - for "anytime" we need to check all flexible slots
    // - for a specific time, exclude flexible slots because they might share the same time string as fixed slots if
    //  these start at the same hour due to partner's start hour config
    const slots = time === 'Anytime'
      ? this.availabilityForSelectedDate.filter((slot) => slot.duration?.length?.unit === 'FLEXIBLE')
      : this.availabilityForSelectedDate.filter((slot) => slot.time === time && slot.duration?.length?.unit !== 'FLEXIBLE')

    slots.forEach((slot) => uniqueStatuses.add(slot.status))
    return Array.from(uniqueStatuses)
  }

  doesTimeRequireMinimumTickets(time: string): boolean {
    const statuses = this.getStatusesForTime(time)
    return statuses.length > 0 && statuses.every((status) => status === 'MIN_REQUIRED_NOT_BOOKABLE')
  }

  /**
   * Checks the given status against the list of available statuses.
   * "Available" means the timeslot represents real availability in the system.
   *
   * (e.g. capacity exists, not sold out, not blocked).
   *
   * A pure passthrough to `availability/status.ts` with no added logic — it exists only so
   * templates can bind the predicate through `@context`, which cannot reach a bare module export.
   */
  isAvailableStatus(status: AvailabilityMode): boolean {
    return isAvailableStatus(status)
  }

  /**
   * Checks the given status against the list of bookable statuses.
   * "Bookable" means that the timeslot is SELECTABLE from within the flow.
   *
   * (e.g. CALL_TO_BOOK is not bookable in the flow, despite being available. A user must call to book instead)
   *
   * A pure passthrough, for the same `@context`-binding reason as `isAvailableStatus` above.
   */
  isBookableStatus(status: AvailabilityMode): boolean {
    return isBookableStatus(status)
  }

  /**
   * Determines whether a time string has availability for the selected date.
   */
  isTimeAvailable(timeString: string | 'Anytime'): boolean {
    if (timeString === 'Anytime') return true
    return this.availabilityForSelectedDate?.some((slot) => slot.time === timeString && this.isAvailableStatus(slot.status))
  }

  /**
   * Whether the currently-selected time is still available for the selected date. 'Anytime' needs
   * at least one flexible slot (isTimeAvailable('Anytime') is always true, so it can't answer this);
   * a fixed time must exist and be available. No selection is not available. This is the derivation
   * behind both "can we advance the time step" and onFlowUpdated's clear-if-unavailable check.
   */
  get isSelectedTimeAvailable(): boolean {
    const selected = this.selectedAvailabilityTime
    if (!selected) return false

    if (selected === 'Anytime') {
      return this.getFilteredAvailability({ hideBookedOut: true }).some(
        (slot) => slot.duration?.length?.unit === 'FLEXIBLE'
      )
    }

    return this.isTimeAvailable(selected)
  }

  /**
   * Determines whether a duration has availability for the selected date and time.
   */
  isDurationAvailable(durationName: string): boolean {
    return (
      this.availabilityForSelectedDate?.some(
        (slot) =>
          slot.time === this.selectedAvailabilityTime &&
          slot.duration.name === durationName &&
          this.isAvailableStatus(slot.status)
      ) ?? false
    )
  }

  /**
   * Determines whether a duration matches the currently selected duration filter.
   * If no duration filter is selected, all durations match.
   */
  matchesSelectedDuration(duration: { name: string }): boolean {
    if (!duration) return false

    return (
      !this.selectedAvailabilityDuration ||
      duration.name === this.selectedAvailabilityDuration
    )
  }

  /**
   * Determines whether a time matches the currently selected time filter.
   * An unset selection or "Anytime" matches all times.
   */
  matchesSelectedTime(time: string): boolean {
    return (
      this.selectedAvailabilityTime === 'Anytime' ||
      !this.selectedAvailabilityTime ||
      time === this.selectedAvailabilityTime
    )
  }

  /**
   * Builds a map of required ticket quantities keyed by resource option ID,
   * based on the currently selected and confirmed tickets.
   */
  getConfirmedTicketCounts(): Record<string, number> {
    return this.selectedTickets.reduce((acc, { resourceOptionId, reservationStatus }) => {
      if (reservationStatus !== 'CONFIRMED' || !resourceOptionId) return acc

      acc[resourceOptionId] = (acc[resourceOptionId] ?? 0) + 1
      return acc
    }, {} as Record<string, number>)
  }

  /**
   * Determines whether a timeslot can fulfill the quantity requirements of all selected tickets.
   *
   * For each required ticket (keyed by resourceOptionId), this method:
   * - looks at all availability entries for that ticket within the timeslot
   * - determines the maximum quantity available for that ticket
   * - verifies that the maximum available quantity meets or exceeds
   *   the number of selected (confirmed) tickets
   *
   * If no tickets are required, the timeslot is always considered valid.
   */
  hasAvailabilityForSelectedTickets(
    slot: AvailabilityTime,
    requiredQtyByTicketId: Record<string, number>
  ): boolean {
    // Rental equipment types have independent per-type inventory. The slot-level status can
    // be set to NOT_AVAILABLE when one type is exhausted, which would incorrectly block
    // all other independent types. Per-type capacity is enforced via getMaxAvailableQtyForTicket.
    if (this.isRental) return true
    return this.activityDomain.hasAvailabilityForSelectedTickets(slot, requiredQtyByTicketId)
  }
}

declare global {
  export type AvailabilityStore = AvailabilityStoreClass

  export interface StoreRegistry {
    availability: AvailabilityStore
  }
}
